import { useMemo, useState } from 'react';
import { router } from '@inertiajs/react';
import SessionShell from '../../../../components/vocabulary/shared/SessionShell';
import { Button } from '../../../../components/ui';
import { Check, ChevronLeft } from 'lucide-react';

const GROUP_LABELS = {
  course: 'Course lists',
  awl: 'Assigned: Academic Word List',
  sat: 'Assigned: SAT / SSAT',
  ngsl: 'Assigned: frequency lists',
  curated: 'Assigned: curated lists',
  custom: 'Assigned by your teacher',
  generated: 'Assigned by your teacher',
  personal: 'Words you\'ve added',
};

const GROUP_ORDER = [
  'course',
  'awl',
  'sat',
  'ngsl',
  'curated',
  'custom',
  'generated',
  'personal',
];

const BATCH_SIZES = [5, 10, 15, 20];

const DEFAULT_SECONDS_PER_WORD = 75;

export default function OnboardingConfigure({ lists, suggested_batch_size = 5, seconds_per_word }) {
  const [selectedIds, setSelectedIds] = useState(() =>
    lists[0]?.id ? new Set([lists[0].id]) : new Set()
  );
  const [batchSize, setBatchSize] = useState(suggested_batch_size);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const csrfToken =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.content
      : '';

  const grouped = useMemo(() => {
    const buckets = {};
    for (const list of lists) {
      const key = GROUP_LABELS[list.list_type] ? list.list_type : 'generated';
      (buckets[key] ||= []).push(list);
    }
    if (buckets.custom && buckets.generated) {
      buckets.custom = buckets.custom.concat(buckets.generated);
      delete buckets.generated;
    }
    return GROUP_ORDER.filter((k) => buckets[k]).map((k) => ({
      key: k,
      label: GROUP_LABELS[k] || k,
      lists: buckets[k].sort((a, b) => b.pending_count - a.pending_count),
    }));
  }, [lists]);

  const totalSelectedPending = useMemo(() => {
    return lists
      .filter((l) => selectedIds.has(l.id))
      .reduce((sum, l) => sum + (l.pending_count || 0), 0);
  }, [lists, selectedIds]);

  // Live estimate for the batch about to be started (§4.2). Falls back to a
  // flat 75s/word pace until the student has a completed onboarding session
  // to derive a personal rolling average from.
  const estimatedMinutes = useMemo(() => {
    const wordCount = Math.min(batchSize, totalSelectedPending || batchSize);
    const pace = seconds_per_word || DEFAULT_SECONDS_PER_WORD;
    return Math.max(1, Math.ceil((wordCount * pace) / 60));
  }, [batchSize, totalSelectedPending, seconds_per_word]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStart = async () => {
    if (selectedIds.size === 0 || starting) return;

    const chosen = lists.filter((l) => selectedIds.has(l.id));
    const vocabulary_list_ids = chosen
      .filter((c) => c.scope === 'list')
      .map((c) => c.list_id);
    const source_types = chosen
      .filter((c) => c.scope === 'personal')
      .map((c) => c.source_type);

    setStarting(true);
    setError(null);

    try {
      const res = await fetch('/student/vocabulary/onboarding_sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          vocabulary_list_ids,
          source_types,
          batch_size: batchSize,
        }),
      });
      const data = await res.json();
      if (data.session_id) {
        router.visit(`/student/vocabulary/onboarding_sessions/${data.session_id}`);
      } else {
        setError(data.error || 'Could not start session');
        setStarting(false);
      }
    } catch (e) {
      setError(e.message || 'Could not start session');
      setStarting(false);
    }
  };

  if (lists.length === 0) {
    return (
      <SessionShell topLabel="Choose lists">
        <div className="text-center py-12 space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            No words pending onboarding right now. Add some words from a book or pick a curated list.
          </p>
          <Button onClick={() => router.visit('/student/dictionary')}>Back to dictionary</Button>
        </div>
      </SessionShell>
    );
  }

  const buttonLabel = starting
    ? 'Starting...'
    : selectedIds.size === 0
    ? 'Pick at least one list'
    : `Begin session (${Math.min(batchSize, totalSelectedPending)} words)`;

  return (
    <SessionShell
      topLabel="Choose lists"
      topRight={
        <button
          onClick={() => router.visit('/student/dictionary')}
          className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back
        </button>
      }
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Which lists do you want to practice?
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Pick one or more — we'll pull your batch from across them.
          </p>
        </div>

        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.key}>
              <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                {group.label}
              </h3>
              <div className="space-y-2">
                {group.lists.map((list) => {
                  const isSelected = selectedIds.has(list.id);
                  return (
                    <button
                      key={list.id}
                      onClick={() => toggle(list.id)}
                      disabled={starting}
                      className={`
                        w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center justify-between gap-3
                        ${
                          isSelected
                            ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-gray-800'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span
                          className={`
                            w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                            ${
                              isSelected
                                ? 'bg-gray-900 border-gray-900 dark:bg-white dark:border-white'
                                : 'border-gray-300 dark:border-gray-600'
                            }
                          `}
                          aria-hidden="true"
                        >
                          {isSelected && (
                            <Check className="w-3 h-3 text-white dark:text-gray-900" strokeWidth={3} />
                          )}
                        </span>
                        <span className="text-sm text-gray-900 dark:text-white truncate">
                          {list.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {list.pending_count}{' '}
                        {list.pending_count === 1 ? 'word' : 'words'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
              Batch size
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ~{estimatedMinutes} {estimatedMinutes === 1 ? 'min' : 'mins'}
            </span>
          </div>
          <div className="flex items-center gap-2" role="radiogroup" aria-label="Batch size">
            {BATCH_SIZES.map((n) => {
              const isActive = batchSize === n;
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => setBatchSize(n)}
                  disabled={starting}
                  className={`
                    px-4 py-2 rounded-lg border text-sm transition-colors
                    ${
                      isActive
                        ? 'border-gray-900 dark:border-white bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                        : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                    }
                  `}
                >
                  {n}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="sticky bottom-0 bg-white dark:bg-gray-950 pt-4 pb-4 border-t border-gray-100 dark:border-gray-800">
          <Button
            onClick={handleStart}
            disabled={selectedIds.size === 0 || starting}
            className="glow-button w-full"
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
    </SessionShell>
  );
}
